/**
 * Utility functions for v2 agent message rendering
 */

import { getCustomMcpServerDisplayName } from "src/components/ui/chat/tool-calls/utils";

/**
 * Truncate a string to max_length characters, adding '...' if truncated.
 */
function truncateString(str: string, maxLength: number = 100): string {
  if (str.length > maxLength) {
    return str.substring(0, maxLength) + "...";
  }
  return str;
}

/**
 * Get display name for tools in v2 agent messages
 */
export function getV2ToolDisplayName(toolName: string, rawInput?: string | null): string {
  // Special case for MCP tools
  if (toolName === "mcp__tierzero__query_datadog_logs") {
    return "QueryDatadogLogs";
  }

  if (toolName === "mcp__tierzero__query_datadog_logs_aggregation") {
    return "QueryDatadogLogsAggregation";
  }

  if (toolName === "mcp__tierzero__query_datadog_spans") {
    return "QueryDatadogSpans";
  }

  if (toolName === "mcp__tierzero__query_datadog_spans_aggregation") {
    return "QueryDatadogSpansAggregation";
  }

  if (toolName === "mcp__tierzero__query_datadog_ci_pipeline_events") {
    return "QueryDatadogCIPipelineEvents";
  }

  if (toolName === "mcp__tierzero__query_datadog_ci_pipeline_events_aggregation") {
    return "QueryDatadogCIPipelineEventsAggregation";
  }

  if (toolName === "mcp__tierzero__query_datadog_rum_aggregation") {
    return "QueryDatadogRUMAggregation";
  }

  if (toolName === "mcp__tierzero__query_datadog_events") {
    return "QueryDatadogEvents";
  }

  if (toolName === "mcp__tierzero__query_datadog_error_tracking_issue_details") {
    return "QueryDatadogErrorTrackingIssueDetails";
  }

  if (toolName === "mcp__tierzero__query_datadog_error_tracking_issues") {
    return "QueryDatadogErrorTrackingIssues";
  }

  if (toolName === "mcp__tierzero__query_datadog_dashboard") {
    return "QueryDatadogDashboard";
  }

  if (toolName === "mcp__tierzero__query_datadog_notebook") {
    return "QueryDatadogNotebook";
  }

  if (toolName === "mcp__tierzero__search_datadog_notebooks") {
    return "SearchDatadogNotebooks";
  }

  if (toolName === "mcp__tierzero__query_datadog_monitor") {
    return "QueryDatadogMonitor";
  }

  if (toolName === "mcp__tierzero__query_datadog_incident") {
    return "QueryDatadogIncident";
  }

  // Datadog Synthetic Tests
  if (toolName === "mcp__tierzero__search_datadog_synthetic_tests") {
    return "SearchDatadogSyntheticTests";
  }
  if (toolName === "mcp__tierzero__query_datadog_synthetic_test") {
    return "QueryDatadogSyntheticTest";
  }
  if (toolName === "mcp__tierzero__query_datadog_synthetic_test_results") {
    return "QueryDatadogSyntheticTestResults";
  }
  if (toolName === "mcp__tierzero__query_datadog_synthetic_test_result") {
    return "QueryDatadogSyntheticTestResult";
  }
  if (toolName === "mcp__tierzero__query_datadog_synthetic_ci_batch") {
    return "QueryDatadogSyntheticCIBatch";
  }

  if (toolName === "mcp__tierzero__query_datadog_service_dependency") {
    return "QueryDatadogServiceDependency";
  }

  if (toolName === "mcp__tierzero__query_datadog_metrics") {
    return "QueryDatadogMetrics";
  }

  if (toolName === "mcp__tierzero__search_datadog_metrics") {
    return "SearchDatadogMetrics";
  }

  if (toolName === "mcp__tierzero__query_datadog_metric_tags") {
    return "QueryDatadogMetricTags";
  }

  if (toolName === "mcp__tierzero__query_datadog_slos") {
    return "Get Service Level Objectives (SLOs)";
  }

  if (toolName === "mcp__tierzero__query_datadog_slos_performance") {
    return "SLO Performance";
  }

  if (toolName === "mcp__tierzero__query_datadog_slo_daily_performance") {
    return "SLO Daily Performance";
  }

  // Default MCP tools
  if (toolName === "mcp__tierzero__get_relevant_context") {
    return "GetRelevantContext";
  }

  if (toolName === "mcp__tierzero__save_snippet") {
    return "SaveSnippet";
  }

  // Notion MCP tools
  if (toolName === "mcp__tierzero__query_notion_documents") {
    return "QueryNotionDocuments";
  }

  if (toolName === "mcp__tierzero__search_notion_documents") {
    return "SearchNotionDocuments";
  }

  // Confluence MCP tools
  if (toolName === "mcp__tierzero__query_confluence_documents") {
    return "QueryConfluenceDocuments";
  }

  if (toolName === "mcp__tierzero__search_confluence_documents") {
    return "SearchConfluenceDocuments";
  }

  // Buildkite MCP tools
  if (toolName === "mcp__tierzero__query_buildkite_builds") {
    return "QueryBuildkiteBuilds";
  }

  if (toolName === "mcp__tierzero__query_buildkite_logs") {
    return "QueryBuildkiteLogs";
  }

  if (toolName === "mcp__tierzero__query_buildkite_annotations") {
    return "QueryBuildkiteAnnotations";
  }

  if (toolName === "mcp__tierzero__query_buildkite_artifacts") {
    return "QueryBuildkiteArtifacts";
  }

  if (toolName === "mcp__tierzero__query_buildkite_flaky_test") {
    return "QueryBuildkiteFlakyTest";
  }

  // CloudWatch MCP tools
  if (toolName === "mcp__tierzero__query_cloudwatch_logs") {
    return "QueryCloudWatchLogs";
  }

  if (toolName === "mcp__tierzero__query_cloudwatch_logs_aggregation") {
    return "QueryCloudWatchLogsAggregation";
  }

  if (toolName === "mcp__tierzero__query_cloudwatch_metrics") {
    return "QueryCloudWatchMetrics";
  }

  // GCP Cloud Monitoring MCP tools
  if (toolName === "mcp__tierzero__query_gcp_cloud_monitoring_logs") {
    return "QueryGCPCloudMonitoringLogs";
  }

  if (toolName === "mcp__tierzero__query_gcp_cloud_monitoring_metrics") {
    return "QueryGCPCloudMonitoringMetrics";
  }

  if (toolName === "mcp__tierzero__query_gcp_cloud_monitoring_log_buckets") {
    return "QueryGCPCloudMonitoringLogBuckets";
  }

  if (toolName === "mcp__tierzero__query_gcp_cloud_monitoring_logs_aggregation") {
    return "QueryGCPCloudMonitoringLogsAggregation";
  }

  if (toolName === "mcp__tierzero__query_gcp_cloud_monitoring_dashboard") {
    return "QueryGCPCloudMonitoringDashboard";
  }

  // Grafana Cloud MCP tools
  if (toolName === "mcp__tierzero__query_grafana_cloud_logs") {
    return "QueryGrafanaLogs";
  }

  if (toolName === "mcp__tierzero__query_grafana_cloud_logs_aggregation") {
    return "QueryGrafanaLogsAggregation";
  }

  if (toolName === "mcp__tierzero__query_grafana_cloud_metrics") {
    return "QueryGrafanaMetrics";
  }

  if (toolName === "mcp__tierzero__query_grafana_cloud_spans") {
    return "QueryGrafanaSpans";
  }

  if (toolName === "mcp__tierzero__query_grafana_cloud_spans_aggregation") {
    return "QueryGrafanaSpansAggregation";
  }

  // New Relic MCP tools
  if (toolName === "mcp__tierzero__query_new_relic_logs") {
    return "QueryNewRelicLogs";
  }

  if (toolName === "mcp__tierzero__query_new_relic_logs_aggregation") {
    return "QueryNewRelicLogsAggregation";
  }

  if (toolName === "mcp__tierzero__query_new_relic_metrics") {
    return "QueryNewRelicMetrics";
  }

  if (toolName === "mcp__tierzero__query_new_relic_spans") {
    return "QueryNewRelicSpans";
  }

  if (toolName === "mcp__tierzero__query_new_relic_spans_aggregation") {
    return "QueryNewRelicSpansAggregation";
  }

  if (toolName === "mcp__tierzero__query_new_relic_transactions") {
    return "QueryNewRelicTransactions";
  }

  if (toolName === "mcp__tierzero__query_new_relic_transactions_aggregation") {
    return "QueryNewRelicTransactionsAggregation";
  }

  if (toolName === "mcp__tierzero__query_new_relic_dashboard") {
    return "QueryNewRelicDashboard";
  }

  if (toolName === "mcp__tierzero__query_new_relic_change_tracking") {
    return "QueryNewRelicChangeTracking";
  }

  // Rootly MCP tools
  if (toolName === "mcp__tierzero__query_rootly_dashboard") {
    return "QueryRootlyDashboard";
  }
  if (toolName === "mcp__tierzero__query_rootly_incidents") {
    return "QueryRootlyIncidents";
  }
  if (toolName === "mcp__tierzero__query_rootly_incident") {
    return "QueryRootlyIncident";
  }
  if (toolName === "mcp__tierzero__query_rootly_alerts") {
    return "QueryRootlyAlerts";
  }
  if (toolName === "mcp__tierzero__query_rootly_alert") {
    return "QueryRootlyAlert";
  }
  if (toolName === "mcp__tierzero__query_rootly_incident_alerts") {
    return "QueryRootlyIncidentAlerts";
  }
  if (toolName === "mcp__tierzero__query_rootly_oncall") {
    return "QueryRootlyOncall";
  }
  if (toolName === "mcp__tierzero__update_rootly_incident") {
    return "UpdateRootlyIncident";
  }

  // PagerDuty MCP tools
  if (toolName === "mcp__tierzero__query_pagerduty_incidents") {
    return "QueryPagerDutyIncidents";
  }
  if (toolName === "mcp__tierzero__query_pagerduty_incident") {
    return "QueryPagerDutyIncident";
  }
  if (toolName === "mcp__tierzero__query_pagerduty_incident_alerts") {
    return "QueryPagerDutyIncidentAlerts";
  }
  if (toolName === "mcp__tierzero__query_pagerduty_incident_notes") {
    return "QueryPagerDutyIncidentNotes";
  }

  // FireHydrant MCP tools
  if (toolName === "mcp__tierzero__query_firehydrant_incidents") {
    return "QueryFireHydrantIncidents";
  }
  if (toolName === "mcp__tierzero__query_firehydrant_incident") {
    return "QueryFireHydrantIncident";
  }
  if (toolName === "mcp__tierzero__query_firehydrant_incident_alerts") {
    return "QueryFireHydrantIncidentAlerts";
  }
  if (toolName === "mcp__tierzero__query_firehydrant_incident_external_resources") {
    return "QueryFireHydrantIncidentExternalResources";
  }
  if (toolName === "mcp__tierzero__query_firehydrant_incident_bridge_summaries") {
    return "QueryFireHydrantIncidentBridgeSummaries";
  }
  if (toolName === "mcp__tierzero__query_firehydrant_alerts") {
    return "QueryFireHydrantAlerts";
  }
  if (toolName === "mcp__tierzero__query_firehydrant_alert") {
    return "QueryFireHydrantAlert";
  }
  if (toolName === "mcp__tierzero__query_firehydrant_retrospectives") {
    return "QueryFireHydrantRetrospectives";
  }
  if (toolName === "mcp__tierzero__query_firehydrant_post_mortem_reports") {
    return "QueryFireHydrantPostMortemReports";
  }

  // Incident.io MCP tools
  if (toolName === "mcp__tierzero__query_incident_io_incidents") {
    return "QueryIncidentIoIncidents";
  }
  if (toolName === "mcp__tierzero__query_incident_io_incident") {
    return "QueryIncidentIoIncident";
  }
  if (toolName === "mcp__tierzero__query_incident_io_incident_alerts") {
    return "QueryIncidentIoIncidentAlerts";
  }
  if (toolName === "mcp__tierzero__query_incident_io_alert") {
    return "QueryIncidentIoAlert";
  }

  // Elasticsearch MCP tools
  if (toolName === "mcp__tierzero__search_elasticsearch_logs") {
    return "SearchElasticsearchLogs";
  }

  if (toolName === "mcp__tierzero__aggregate_elasticsearch_logs") {
    return "AggregateElasticsearchLogs";
  }

  if (toolName === "mcp__tierzero__list_elasticsearch_indices") {
    return "ListElasticsearchIndices";
  }

  // Prometheus MCP tools
  if (toolName === "mcp__tierzero__query_prometheus_metrics") {
    return "QueryPrometheusMetrics";
  }

  if (toolName === "mcp__tierzero__list_prometheus_label_values") {
    return "ListPrometheusLabelValues";
  }

  if (toolName === "mcp__tierzero__list_prometheus_targets") {
    return "ListPrometheusTargets";
  }

  // Sentry MCP tools
  if (toolName === "mcp__tierzero__query_sentry_issues") {
    return "QuerySentryIssues";
  }

  if (toolName === "mcp__tierzero__query_sentry_issue_events") {
    return "QuerySentryIssueEvents";
  }

  if (toolName === "mcp__tierzero__query_sentry_trace_details") {
    return "QuerySentryTraceDetails";
  }

  if (toolName === "mcp__tierzero__query_sentry_traces") {
    return "QuerySentryTraces";
  }

  if (toolName === "mcp__tierzero__query_sentry_events") {
    return "QuerySentryEvents";
  }

  if (toolName === "mcp__tierzero__query_sentry_events_aggregation") {
    return "QuerySentryEventsAggregation";
  }

  // Rollbar MCP tools
  if (toolName === "mcp__tierzero__query_rollbar_items") {
    return "QueryRollbarItems";
  }
  if (toolName === "mcp__tierzero__query_rollbar_item") {
    return "QueryRollbarItem";
  }
  if (toolName === "mcp__tierzero__query_rollbar_item_occurrences") {
    return "QueryRollbarItemOccurrences";
  }
  if (toolName === "mcp__tierzero__query_rollbar_occurrence") {
    return "QueryRollbarOccurrence";
  }
  if (toolName === "mcp__tierzero__query_rollbar_project_occurrences") {
    return "QueryRollbarProjectOccurrences";
  }
  if (toolName === "mcp__tierzero__query_rollbar_metrics_occurrences") {
    return "QueryRollbarMetricsOccurrences";
  }
  if (toolName === "mcp__tierzero__query_rollbar_metrics_items") {
    return "QueryRollbarMetricsItems";
  }
  if (toolName === "mcp__tierzero__query_rollbar_top_active_items") {
    return "QueryRollbarTopActiveItems";
  }
  if (toolName === "mcp__tierzero__query_rollbar_occurrence_counts") {
    return "QueryRollbarOccurrenceCounts";
  }
  if (toolName === "mcp__tierzero__query_rollbar_deploys") {
    return "QueryRollbarDeploys";
  }
  if (toolName === "mcp__tierzero__query_rollbar_version") {
    return "QueryRollbarVersion";
  }
  if (toolName === "mcp__tierzero__query_rollbar_version_items") {
    return "QueryRollbarVersionItems";
  }
  if (toolName === "mcp__tierzero__query_rollbar_rql") {
    return "QueryRollbarRQL";
  }
  if (toolName === "mcp__tierzero__submit_rollbar_rql") {
    return "SubmitRollbarRQL";
  }
  if (toolName === "mcp__tierzero__query_rollbar_rql_job") {
    return "QueryRollbarRQLJob";
  }
  if (toolName === "mcp__tierzero__query_rollbar_environments") {
    return "QueryRollbarEnvironments";
  }

  // Slack MCP tools
  if (toolName === "mcp__tierzero__query_slack_channel_contents") {
    return "QuerySlackChannelContents";
  }

  if (toolName === "mcp__tierzero__query_slack_channel_metadata") {
    return "QuerySlackChannelMetadata";
  }

  if (toolName === "mcp__tierzero__get_mention_slack_usergroup") {
    return "GetSlackUsergroupMention";
  }

  if (toolName === "mcp__tierzero__get_mention_slack_user") {
    return "GetSlackUserMention";
  }

  if (toolName === "mcp__tierzero__post_slack_message") {
    return "PostSlackMessage";
  }

  if (toolName === "mcp__tierzero__forward_slack_message") {
    return "ForwardSlackMessage";
  }

  if (toolName === "mcp__tierzero__create_slack_channel") {
    return "CreateSlackChannel";
  }

  // Temporal MCP tools
  if (toolName === "mcp__tierzero__query_temporal_workflows") {
    return "QueryTemporalWorkflows";
  }

  if (toolName === "mcp__tierzero__query_temporal_workflow_history") {
    return "QueryTemporalWorkflowHistory";
  }

  // LangSmith MCP tools
  if (toolName === "mcp__tierzero__query_langsmith_runs") {
    return "QueryLangSmithRuns";
  }

  if (toolName === "mcp__tierzero__get_langsmith_run_stats") {
    return "GetLangSmithRunStats";
  }

  // Langfuse MCP tools
  if (toolName === "mcp__tierzero__query_langfuse_traces") {
    return "QueryLangfuseTraces";
  }

  if (toolName === "mcp__tierzero__get_langfuse_trace") {
    return "GetLangfuseTrace";
  }

  if (toolName === "mcp__tierzero__get_langfuse_metrics") {
    return "GetLangfuseMetrics";
  }

  // Web search MCP tools
  if (toolName === "mcp__tierzero__search_web") {
    return "SearchWeb";
  }

  if (toolName === "mcp__tierzero__fetch_web_content") {
    return "FetchWebContent";
  }

  // Capability execution tool
  if (toolName === "mcp__tierzero__run_capability") {
    return "RunCapability";
  }

  // Custom MCP gateway tools — use server name as display title
  if (toolName === "mcp__tierzero__list_custom_mcp_server_tools" || toolName === "mcp__tierzero__call_custom_mcp_server_tool") {
    if (rawInput) {
      try {
        const parsed = typeof rawInput === "string" ? JSON.parse(rawInput) : rawInput;
        return getCustomMcpServerDisplayName(parsed.server_slug) ?? "Custom MCP Server";
      } catch {
        // fall through
      }
    }
    return "Custom MCP Server";
  }

  // Jira MCP tools
  if (toolName === "mcp__tierzero__get_jira_issue") {
    return "GetJiraIssue";
  }

  if (toolName === "mcp__tierzero__search_jira_issues") {
    return "SearchJiraIssues";
  }
  if (toolName === "mcp__tierzero__create_jira_issue") {
    return "CreateJiraIssue";
  }
  if (toolName === "mcp__tierzero__update_jira_issue") {
    return "UpdateJiraIssue";
  }
  if (toolName === "mcp__tierzero__add_jira_comment") {
    return "AddJiraComment";
  }
  if (toolName === "mcp__tierzero__edit_jira_comment") {
    return "EditJiraComment";
  }
  if (toolName === "mcp__tierzero__list_jira_projects") {
    return "ListJiraProjects";
  }

  // Linear MCP tools
  if (toolName === "mcp__tierzero__search_linear_issues") {
    return "SearchLinearIssues";
  }
  if (toolName === "mcp__tierzero__get_linear_issue") {
    return "GetLinearIssue";
  }
  if (toolName === "mcp__tierzero__create_linear_issue") {
    return "CreateLinearIssue";
  }
  if (toolName === "mcp__tierzero__update_linear_issue") {
    return "UpdateLinearIssue";
  }
  if (toolName === "mcp__tierzero__add_linear_comment") {
    return "AddLinearComment";
  }
  if (toolName === "mcp__tierzero__edit_linear_comment") {
    return "EditLinearComment";
  }
  if (toolName === "mcp__tierzero__list_linear_teams") {
    return "ListLinearTeams";
  }
  if (toolName === "mcp__tierzero__list_linear_users") {
    return "ListLinearUsers";
  }
  if (toolName === "mcp__tierzero__list_linear_projects") {
    return "ListLinearProjects";
  }
  if (toolName === "mcp__tierzero__search_linear_projects") {
    return "SearchLinearProjects";
  }
  if (toolName === "mcp__tierzero__get_linear_project") {
    return "GetLinearProject";
  }
  if (toolName === "mcp__tierzero__create_linear_project") {
    return "CreateLinearProject";
  }
  if (toolName === "mcp__tierzero__update_linear_project") {
    return "UpdateLinearProject";
  }

  // PGAnalyze MCP tools
  if (toolName === "mcp__tierzero__query_pganalyze_server_info") {
    return "QueryPGAnalyzeServerInfo";
  }
  if (toolName === "mcp__tierzero__query_pganalyze_database_info") {
    return "QueryPGAnalyzeDatabaseInfo";
  }
  if (toolName === "mcp__tierzero__query_pganalyze_schema") {
    return "QueryPGAnalyzeSchema";
  }
  if (toolName === "mcp__tierzero__query_pganalyze_queries") {
    return "QueryPGAnalyzeQueries";
  }
  if (toolName === "mcp__tierzero__query_pganalyze_issues") {
    return "QueryPGAnalyzeIssues";
  }
  if (toolName === "mcp__tierzero__query_pganalyze_vacuums") {
    return "QueryPGAnalyzeVacuums";
  }
  if (toolName === "mcp__tierzero__query_pganalyze_logs") {
    return "QueryPGAnalyzeLogs";
  }
  if (toolName === "mcp__tierzero__query_pganalyze_connections") {
    return "QueryPGAnalyzeConnections";
  }

  // Socket tools
  if (toolName === "mcp__tierzero__query_socket_alerts") {
    return "QuerySocketAlerts";
  }
  if (toolName === "mcp__tierzero__list_socket_repos") {
    return "ListSocketRepos";
  }
  if (toolName === "mcp__tierzero__query_socket_scan") {
    return "QuerySocketScan";
  }

  // PostgreSQL tools
  if (toolName === "mcp__tierzero__RunPostgresQuery") {
    return "PostgreSQL";
  }

  // CLI tools — display name based on base_cmd
  if (toolName === "mcp__tierzero__RunCliCmd") {
    if (rawInput) {
      try {
        // rawInput may be a JSON scalar (already parsed) or a string
        const parsed = typeof rawInput === "string" ? JSON.parse(rawInput) : rawInput;
        if (parsed.base_cmd === "gcloud") return "GCP";
        if (parsed.base_cmd === "kubectl") return "Kubernetes";
        if (parsed.base_cmd === "snow") return "Snowflake";
      } catch {
        // fall through
      }
    }
    return "AWS";
  }

  if (toolName === "mcp__tierzero__RunCustomCliCmd") {
    return "Run Custom Command";
  }

  // Optimizer / Autopilot tools
  if (toolName === "mcp__tierzero__propose_optimizer_config") {
    return "ProposeOptimizerConfig";
  }
  if (toolName === "mcp__tierzero__save_optimizer_config") {
    return "SaveOptimizerConfig";
  }
  if (toolName === "mcp__tierzero__save_optimizer_run_results") {
    return "SaveOptimizerRunResults";
  }
  if (toolName === "mcp__tierzero__save_optimizer_cluster_result") {
    return "SaveOptimizerClusterResult";
  }
  if (toolName === "mcp__tierzero__save_proposal_result") {
    return "SaveProposalResult";
  }
  if (toolName === "mcp__tierzero__update_proposal_status") {
    return "UpdateProposalStatus";
  }
  if (toolName === "mcp__tierzero__get_issue_data") {
    return "GetIssueData";
  }
  if (toolName === "mcp__tierzero__deprecate_issue") {
    return "DeprecateIssue";
  }
  if (toolName === "mcp__tierzero__update_issue_description") {
    return "UpdateIssueDescription";
  }

  // Custom MCP server tools: mcp__<server_name>__<tool_name> → <ServerName>: <ToolName>
  const customMcpMatch = toolName.match(/^mcp__(?!tierzero__)([^_]+(?:[_-][^_]+)*)__(.+)$/);
  if (customMcpMatch) {
    const serverName = customMcpMatch[1]
      .split(/[_-]/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join("");
    const toolPart = customMcpMatch[2]
      .split(/[_-]/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join("");
    return `${serverName}: ${toolPart}`;
  }

  // Keep original name for others
  return toolName;
}

/**
 * Extract custom information from tool input for display
 * Returns the relevant field based on tool type
 */
export function extractToolCustomInfo(toolCall: any): string | null {
  const toolName = toolCall.toolUse?.name;
  if (!toolName) return null;

  const input = toolCall.toolUse?.input;
  if (!input) return null;

  // Only extract from GenericToolInput with rawInput
  if (input.__typename !== "GenericToolInput" || !input.rawInput) {
    return null;
  }

  try {
    const rawInput =
      typeof input.rawInput === "string"
        ? JSON.parse(input.rawInput)
        : input.rawInput;

    let displayText: string | null = null;

    switch (toolName) {
      case "mcp__tierzero__query_datadog_logs":
      case "mcp__tierzero__query_datadog_logs_aggregation":
      case "mcp__tierzero__query_datadog_ci_pipeline_events":
      case "mcp__tierzero__query_datadog_ci_pipeline_events_aggregation":
      case "mcp__tierzero__query_datadog_rum_aggregation":
      case "mcp__tierzero__query_datadog_events":
      case "mcp__tierzero__query_datadog_metrics":
        // Parse the query JSON to extract filter.query (flat structure) or show plain text for metrics
        if (rawInput.query) {
          try {
            // Parse the query string (it's a JSON string in V2)
            const queryObj =
              typeof rawInput.query === "string"
                ? JSON.parse(rawInput.query)
                : rawInput.query;

            // Extract filter.query if available
            if (queryObj.filter && queryObj.filter.query) {
              displayText = queryObj.filter.query;
            } else {
              // Fallback to stringified query
              displayText = typeof rawInput.query === "string"
                ? rawInput.query
                : JSON.stringify(rawInput.query);
            }
          } catch {
            // If JSON parsing fails, show raw query string
            displayText = String(rawInput.query);
          }
        }
        break;

      case "mcp__tierzero__query_datadog_spans":
      case "mcp__tierzero__query_datadog_spans_aggregation":
        // Parse the spans query JSON to extract filter.query (nested structure)
        if (rawInput.query) {
          try {
            // Parse the query string (it's a JSON string in V2)
            const queryObj =
              typeof rawInput.query === "string"
                ? JSON.parse(rawInput.query)
                : rawInput.query;

            // Spans queries are nested: data.attributes.filter.query
            if (
              queryObj.data &&
              queryObj.data.attributes &&
              queryObj.data.attributes.filter &&
              queryObj.data.attributes.filter.query
            ) {
              displayText = queryObj.data.attributes.filter.query;
            } else {
              // Fallback to stringified query
              displayText = typeof rawInput.query === "string"
                ? rawInput.query
                : JSON.stringify(rawInput.query);
            }
          } catch {
            // If JSON parsing fails, show raw query string
            displayText = String(rawInput.query);
          }
        }
        break;

      case "mcp__tierzero__query_datadog_error_tracking_issue_details":
        // Issue details tool only has issue_id, not query
        if (rawInput.issue_id) {
          displayText = `issue_id:${rawInput.issue_id}`;
        }
        break;

      case "mcp__tierzero__query_datadog_error_tracking_issues":
        // Issues search has query parameter (plain string, not JSON)
        if (rawInput.query) {
          displayText = String(rawInput.query);
        }
        break;

      case "mcp__tierzero__query_datadog_dashboard":
        // Dashboard tool only has dashboard_id
        if (rawInput.dashboard_id) {
          displayText = `dashboard_id:${rawInput.dashboard_id}`;
        }
        break;

      case "mcp__tierzero__query_datadog_notebook":
        // Notebook tool only has notebook_id
        if (rawInput.notebook_id) {
          displayText = `notebook_id:${rawInput.notebook_id}`;
        }
        break;

      case "mcp__tierzero__search_datadog_notebooks":
        if (rawInput.query) {
          displayText = `query:${rawInput.query}`;
        } else {
          displayText = "(all notebooks)";
        }
        break;

      case "mcp__tierzero__query_datadog_monitor":
        // Monitor tool only has monitor_id
        if (rawInput.monitor_id) {
          displayText = `monitor_id:${rawInput.monitor_id}`;
        }
        break;

      case "mcp__tierzero__query_datadog_incident":
        if (rawInput.incident_id) {
          displayText = `incident_id:${rawInput.incident_id}`;
        }
        break;

      // Datadog Synthetic Tests
      case "mcp__tierzero__search_datadog_synthetic_tests":
        if (rawInput.query) {
          displayText = `query:${rawInput.query}`;
        } else {
          displayText = "(all tests)";
        }
        break;

      case "mcp__tierzero__query_datadog_synthetic_test":
      case "mcp__tierzero__query_datadog_synthetic_test_results":
        if (rawInput.public_id) {
          displayText = `public_id:${rawInput.public_id}`;
        }
        break;

      case "mcp__tierzero__query_datadog_synthetic_test_result":
        if (rawInput.public_id && rawInput.result_id) {
          displayText = `public_id:${rawInput.public_id} result_id:${rawInput.result_id}`;
        }
        break;

      case "mcp__tierzero__query_datadog_synthetic_ci_batch":
        if (rawInput.batch_id) {
          displayText = `batch_id:${rawInput.batch_id}`;
        }
        break;

      case "mcp__tierzero__query_datadog_service_dependency":
        // Service dependency has service_name (no need to show time range in custom info)
        if (rawInput.service_name) {
          displayText = `service:${rawInput.service_name}`;
        }
        break;

      case "mcp__tierzero__search_datadog_metrics":
        // Metrics search has query parameter (search string)
        if (rawInput.query) {
          displayText = `query:${rawInput.query}`;
        }
        break;

      case "mcp__tierzero__query_datadog_metric_tags":
        // Metric tags has metric_name parameter
        if (rawInput.metric_name) {
          displayText = `metric:${rawInput.metric_name}`;
        }
        break;

      case "mcp__tierzero__query_datadog_slos_performance": {
        const parts: string[] = [];
        if (rawInput.start_iso_str && rawInput.end_iso_str) {
          parts.push(`${rawInput.start_iso_str} → ${rawInput.end_iso_str}`);
        }
        if (rawInput.query) {
          parts.push(`query:${rawInput.query}`);
        }
        if (rawInput.tags_query) {
          parts.push(`tags:${rawInput.tags_query}`);
        }
        if (rawInput.ids && Array.isArray(rawInput.ids) && rawInput.ids.length) {
          parts.push(`ids:${rawInput.ids.length}`);
        }
        displayText = parts.join(" | ");
        break;
      }

      case "mcp__tierzero__query_datadog_slo_daily_performance": {
        const parts: string[] = [];
        if (rawInput.slo_id) {
          parts.push(`SLO:${rawInput.slo_id}`);
        }
        if (rawInput.start_iso_str && rawInput.end_iso_str) {
          parts.push(`${rawInput.start_iso_str} → ${rawInput.end_iso_str}`);
        }
        displayText = parts.join(" | ");
        break;
      }

      // Default tools
      case "mcp__tierzero__get_relevant_context":
        // Show the query parameter
        if (rawInput.query) {
          displayText = String(rawInput.query);
        }
        break;

      case "mcp__tierzero__save_snippet": {
        // Show the snippet content and its scope instead of raw JSON.
        const parts = [rawInput.content, rawInput.scope].filter(Boolean).map(String);
        if (parts.length > 0) {
          displayText = parts.join(" | ");
        }
        break;
      }

      // Buildkite tools
      case "mcp__tierzero__query_buildkite_builds":
        if (rawInput.query_params) {
          displayText = `pipeline:${rawInput.pipeline_slug || ""} query_params:${rawInput.query_params || ""}`;
        } else {
          displayText = `pipeline:${rawInput.pipeline_slug || ""} build:${rawInput.build_number || ""}`;
        }
        break;

      case "mcp__tierzero__query_buildkite_logs":
      case "mcp__tierzero__query_buildkite_annotations":
        displayText = `pipeline:${rawInput.pipeline_slug || ""} build:${rawInput.build_number || ""}`;
        break;

      case "mcp__tierzero__query_buildkite_artifacts":
        if (rawInput.pipeline_slug && rawInput.build_number) {
          const parts = [
            `pipeline:${rawInput.pipeline_slug}`,
            `build:${rawInput.build_number}`
          ];
          if (rawInput.job_name) {
            parts.push(`job:${rawInput.job_name}`);
          }
          if (rawInput.step_key) {
            parts.push(`step:${rawInput.step_key}`);
          }
          displayText = parts.join(" ");
        }
        break;

      case "mcp__tierzero__query_buildkite_flaky_test":
        if (rawInput.test_suite_slug && rawInput.test_name) {
          displayText = `suite:${rawInput.test_suite_slug} | test:${rawInput.test_name}`;
        }
        break;

      // CloudWatch tools
      case "mcp__tierzero__query_cloudwatch_logs":
        if (rawInput.query) {
          // Replace both actual newlines and escaped newlines
          const queryStr = (rawInput.query || "").replace(/\\n|\n/g, " ");
          displayText = queryStr;
        }
        break;

      case "mcp__tierzero__query_cloudwatch_logs_aggregation":
        if (rawInput.query) {
          // Replace both actual newlines and escaped newlines
          const queryStr = (rawInput.query || "").replace(/\\n|\n/g, " ");
          displayText = queryStr;
        }
        break;

      case "mcp__tierzero__query_cloudwatch_metrics":
        if (rawInput.query) {
          // Replace all newlines with spaces
          displayText = String(rawInput.query).replace("\\n", " ");
        }
        break;

      // GCP Cloud Monitoring tools
      case "mcp__tierzero__query_gcp_cloud_monitoring_logs":
        if (rawInput.query) {
          displayText = String(rawInput.query);
        }
        break;

      case "mcp__tierzero__query_gcp_cloud_monitoring_metrics":
        if (rawInput.query) {
          displayText = String(rawInput.query);
        }
        break;

      case "mcp__tierzero__query_gcp_cloud_monitoring_log_buckets":
        // Discovery tool with no parameters
        displayText = "(Discovering log buckets)";
        break;

      case "mcp__tierzero__query_gcp_cloud_monitoring_logs_aggregation":
        if (rawInput.query) {
          // Replace newlines with spaces for SQL query display
          displayText = String(rawInput.query).replace(/\n/g, " ");
        }
        break;

      case "mcp__tierzero__query_gcp_cloud_monitoring_dashboard":
        if (rawInput.dashboard_id) {
          displayText = `dashboard_id:${rawInput.dashboard_id}`;
        }
        break;

      // Grafana Cloud tools
      case "mcp__tierzero__query_grafana_cloud_logs":
      case "mcp__tierzero__query_grafana_cloud_logs_aggregation":
      case "mcp__tierzero__query_grafana_cloud_spans":
      case "mcp__tierzero__query_grafana_cloud_spans_aggregation":
        if (rawInput.query) {
          try {
            const parsedQuery = typeof rawInput.query === "string"
              ? JSON.parse(rawInput.query)
              : rawInput.query;
            if (parsedQuery && typeof parsedQuery === "object" && parsedQuery.filter?.query) {
              displayText = parsedQuery.filter.query;
            } else {
              displayText = String(rawInput.query);
            }
          } catch {
            displayText = String(rawInput.query);
          }
        }
        break;

      case "mcp__tierzero__query_grafana_cloud_metrics":
        if (rawInput.query) {
          displayText = String(rawInput.query);
        }
        break;

      // New Relic tools
      case "mcp__tierzero__query_new_relic_logs":
      case "mcp__tierzero__query_new_relic_logs_aggregation":
      case "mcp__tierzero__query_new_relic_spans":
      case "mcp__tierzero__query_new_relic_spans_aggregation":
      case "mcp__tierzero__query_new_relic_transactions":
      case "mcp__tierzero__query_new_relic_transactions_aggregation":
        if (rawInput.query) {
          try {
            const parsedQuery = typeof rawInput.query === "string"
              ? JSON.parse(rawInput.query)
              : rawInput.query;
            if (parsedQuery && typeof parsedQuery === "object" && parsedQuery.filter?.query) {
              displayText = parsedQuery.filter.query;
            } else {
              displayText = String(rawInput.query);
            }
          } catch {
            displayText = String(rawInput.query);
          }
        }
        break;

      case "mcp__tierzero__query_new_relic_metrics":
      case "mcp__tierzero__query_new_relic_change_tracking":
        if (rawInput.query) {
          displayText = String(rawInput.query);
        }
        break;

      case "mcp__tierzero__query_new_relic_dashboard":
        if (rawInput.dashboard_slug) {
          displayText = `dashboard_slug:${rawInput.dashboard_slug}`;
        } else if (rawInput.dashboard_id) {
          displayText = `dashboard_id:${rawInput.dashboard_id}`;
        }
        break;

      // Rootly dashboard tool
      case "mcp__tierzero__query_rootly_dashboard":
        // Show dashboard slug if available, otherwise dashboard ID
        if (rawInput.dashboard_slug) {
          displayText = `dashboard_slug:${rawInput.dashboard_slug}`;
        } else if (rawInput.dashboard_id) {
          displayText = `dashboard_id:${rawInput.dashboard_id}`;
        }
        break;

      // Rootly update incident tool
      case "mcp__tierzero__update_rootly_incident":
        if (rawInput.incident_id) {
          displayText = `incident:${rawInput.incident_id}`;
        }
        break;

      // Rootly on-call tool
      case "mcp__tierzero__query_rootly_oncall":
        displayText = rawInput.user_filter
          ? `user:${rawInput.user_filter}`
          : rawInput.schedule_id
            ? `schedule_id:${rawInput.schedule_id}`
            : "(all schedules)";
        break;

      // PagerDuty tools
      case "mcp__tierzero__query_pagerduty_incidents":
        if (rawInput.query) {
          displayText = `query:${rawInput.query}`;
        } else {
          displayText = "(all incidents)";
        }
        break;

      case "mcp__tierzero__query_pagerduty_incident":
      case "mcp__tierzero__query_pagerduty_incident_alerts":
      case "mcp__tierzero__query_pagerduty_incident_notes":
        if (rawInput.incident_id) {
          displayText = `incident_id:${rawInput.incident_id}`;
        }
        break;

      // FireHydrant tools
      case "mcp__tierzero__query_firehydrant_incidents":
        if (rawInput.query) {
          displayText = `query:${rawInput.query}`;
        } else {
          displayText = "(all incidents)";
        }
        break;

      case "mcp__tierzero__query_firehydrant_incident":
      case "mcp__tierzero__query_firehydrant_incident_alerts":
      case "mcp__tierzero__query_firehydrant_incident_external_resources":
      case "mcp__tierzero__query_firehydrant_incident_bridge_summaries":
        if (rawInput.incident_id) {
          displayText = `incident_id:${rawInput.incident_id}`;
        }
        break;

      case "mcp__tierzero__query_firehydrant_alerts":
        displayText = "(all alerts)";
        break;

      case "mcp__tierzero__query_firehydrant_alert":
        if (rawInput.alert_id) {
          displayText = `alert_id:${rawInput.alert_id}`;
        }
        break;

      case "mcp__tierzero__query_firehydrant_retrospectives":
      case "mcp__tierzero__query_firehydrant_post_mortem_reports":
        if (rawInput.report_id || rawInput.retrospective_id) {
          displayText = `id:${rawInput.report_id || rawInput.retrospective_id}`;
        }
        break;

      // Incident.io tools
      case "mcp__tierzero__query_incident_io_incidents":
        if (rawInput.query) {
          displayText = `query:${rawInput.query}`;
        } else {
          displayText = "(all incidents)";
        }
        break;

      case "mcp__tierzero__query_incident_io_incident":
      case "mcp__tierzero__query_incident_io_incident_alerts":
        if (rawInput.incident_id) {
          displayText = `incident_id:${rawInput.incident_id}`;
        }
        break;

      case "mcp__tierzero__query_incident_io_alert":
        if (rawInput.alert_id) {
          displayText = `alert_id:${rawInput.alert_id}`;
        }
        break;

      // Sentry tools
      case "mcp__tierzero__query_sentry_issues":
        if (rawInput.project_id) {
          const parts = [
            `project:${rawInput.project_id}`,
            `env:${rawInput.environment}`
          ];
          if (rawInput.query_params) {
            parts.push(`query:${rawInput.query_params}`);
          }
          displayText = parts.join(" | ");
        }
        break;

      case "mcp__tierzero__query_sentry_issue_events":
        if (rawInput.issue_id) {
          displayText = `issue_id:${rawInput.issue_id}`;
        }
        break;

      case "mcp__tierzero__query_sentry_trace_details":
        if (rawInput.trace_id) {
          displayText = `trace_id:${rawInput.trace_id}`;
        }
        break;

      case "mcp__tierzero__query_sentry_traces": {
        const parts: string[] = [];
        if (rawInput.query) {
          parts.push(`query:${rawInput.query}`);
        }
        parts.push(`period:${rawInput.stats_period || "14d"}`);
        displayText = parts.join(" | ");
        break;
      }

      case "mcp__tierzero__query_sentry_events": {
        const parts: string[] = [`dataset:${rawInput.dataset || "errors"}`];
        if (rawInput.query) {
          parts.push(`query:${rawInput.query}`);
        }
        if (rawInput.start && rawInput.end) {
          parts.push(`range:${rawInput.start}→${rawInput.end}`);
        } else {
          parts.push(`period:${rawInput.stats_period || "14d"}`);
        }
        displayText = parts.join(" | ");
        break;
      }

      case "mcp__tierzero__query_sentry_events_aggregation": {
        const parts: string[] = [`dataset:${rawInput.dataset || "errors"}`];
        if (rawInput.query) {
          parts.push(`query:${rawInput.query}`);
        }
        if (rawInput.y_axis) {
          parts.push(`y_axis:${rawInput.y_axis}`);
        }
        if (rawInput.interval_seconds) {
          parts.push(`interval:${rawInput.interval_seconds}s`);
        }
        if (rawInput.group_by) {
          parts.push(`group_by:${rawInput.group_by}`);
        }
        if (rawInput.start && rawInput.end) {
          parts.push(`range:${rawInput.start}→${rawInput.end}`);
        } else {
          parts.push(`period:${rawInput.stats_period || "14d"}`);
        }
        displayText = parts.join(" | ");
        break;
      }

      // Rollbar tools
      case "mcp__tierzero__query_rollbar_items": {
        const parts: string[] = [];
        if (rawInput.status) parts.push(`status:${rawInput.status}`);
        if (rawInput.level) parts.push(`level:${rawInput.level}`);
        if (rawInput.environment) parts.push(`env:${rawInput.environment}`);
        if (rawInput.query) parts.push(`query:${rawInput.query}`);
        if (parts.length > 0) displayText = parts.join(" | ");
        break;
      }

      case "mcp__tierzero__query_rollbar_item":
        if (rawInput.item_counter !== undefined) {
          displayText = `item_counter:${rawInput.item_counter}`;
        }
        break;

      case "mcp__tierzero__query_rollbar_item_occurrences":
        if (rawInput.item_counter !== undefined) {
          displayText = `item_counter:${rawInput.item_counter} | page:${rawInput.page ?? 1}`;
        }
        break;

      case "mcp__tierzero__query_rollbar_occurrence":
        if (rawInput.occurrence_id !== undefined) {
          displayText = `occurrence_id:${rawInput.occurrence_id}`;
        }
        break;

      case "mcp__tierzero__query_rollbar_project_occurrences": {
        const parts: string[] = [];
        if (rawInput.environment) parts.push(`env:${rawInput.environment}`);
        if (rawInput.query) parts.push(`query:${rawInput.query}`);
        if (parts.length > 0) displayText = parts.join(" | ");
        break;
      }

      case "mcp__tierzero__query_rollbar_metrics_occurrences": {
        const parts: string[] = [];
        if (rawInput.start_time_unix && rawInput.end_time_unix) {
          parts.push(`range:${rawInput.start_time_unix}→${rawInput.end_time_unix}`);
        }
        if (rawInput.environments?.length) parts.push(`envs:${rawInput.environments.join(",")}`);
        if (rawInput.levels?.length) parts.push(`levels:${rawInput.levels.join(",")}`);
        if (rawInput.group_by?.length) parts.push(`group_by:${rawInput.group_by.join(",")}`);
        if (parts.length > 0) displayText = parts.join(" | ");
        break;
      }

      case "mcp__tierzero__query_rollbar_metrics_items": {
        const parts: string[] = [];
        if (rawInput.start_time_unix && rawInput.end_time_unix) {
          parts.push(`range:${rawInput.start_time_unix}→${rawInput.end_time_unix}`);
        }
        if (rawInput.item_counters?.length) parts.push(`items:${rawInput.item_counters.join(",")}`);
        if (rawInput.environments?.length) parts.push(`envs:${rawInput.environments.join(",")}`);
        if (rawInput.levels?.length) parts.push(`levels:${rawInput.levels.join(",")}`);
        if (parts.length > 0) displayText = parts.join(" | ");
        break;
      }

      case "mcp__tierzero__query_rollbar_top_active_items": {
        const parts: string[] = [`hours:${rawInput.hours ?? 24}`];
        if (rawInput.environments?.length) parts.push(`envs:${rawInput.environments.join(",")}`);
        displayText = parts.join(" | ");
        break;
      }

      case "mcp__tierzero__query_rollbar_occurrence_counts": {
        const parts: string[] = [];
        if (rawInput.environments?.length) parts.push(`envs:${rawInput.environments.join(",")}`);
        if (rawInput.min_level) parts.push(`min_level:${rawInput.min_level}`);
        if (rawInput.max_level) parts.push(`max_level:${rawInput.max_level}`);
        if (parts.length > 0) displayText = parts.join(" | ");
        break;
      }

      case "mcp__tierzero__query_rollbar_deploys": {
        const parts: string[] = [];
        if (rawInput.environment) parts.push(`env:${rawInput.environment}`);
        if (rawInput.before) parts.push(`before:${rawInput.before}`);
        if (rawInput.after) parts.push(`after:${rawInput.after}`);
        parts.push(`page:${rawInput.page ?? 1}`);
        displayText = parts.join(" | ");
        break;
      }

      case "mcp__tierzero__query_rollbar_version":
        if (rawInput.version) {
          displayText = `version:${rawInput.version} | env:${rawInput.environment}`;
        }
        break;

      case "mcp__tierzero__query_rollbar_version_items":
        if (rawInput.version) {
          displayText = `version:${rawInput.version} | env:${rawInput.environment}`;
        }
        break;

      case "mcp__tierzero__query_rollbar_rql":
        if (rawInput.query) {
          displayText = `query:${rawInput.query}`;
        }
        break;

      case "mcp__tierzero__submit_rollbar_rql":
        if (rawInput.query) {
          displayText = `query:${rawInput.query}`;
        }
        break;

      case "mcp__tierzero__query_rollbar_rql_job":
        if (rawInput.job_id !== undefined) {
          displayText = `job_id:${rawInput.job_id}`;
        }
        break;

      case "mcp__tierzero__query_rollbar_environments":
        displayText = "all environments";
        break;

      // Slack tools
      case "mcp__tierzero__query_slack_channel_contents":
        if (rawInput.channel_id) {
          const parts = [`channel:${rawInput.channel_id}`];
          if (rawInput.thread_ts) {
            parts.push(`thread:${rawInput.thread_ts}`);
          }
          displayText = parts.join(" | ");
        }
        break;

      case "mcp__tierzero__query_slack_channel_metadata":
        if (rawInput.channel_name_filter) {
          displayText = `filter:${rawInput.channel_name_filter}`;
        } else {
          displayText = "all channels";
        }
        break;

      case "mcp__tierzero__get_mention_slack_usergroup":
        if (rawInput.usergroup_handle) {
          displayText = `usergroup:${rawInput.usergroup_handle}`;
        }
        break;

      case "mcp__tierzero__get_mention_slack_user":
        if (rawInput.user_name) {
          displayText = `user:${rawInput.user_name}`;
        }
        break;

      case "mcp__tierzero__create_slack_channel": {
        const visibility = rawInput.is_private ? "private" : "public";
        displayText = `${visibility} channel:${rawInput.channel_name || "?"}`;
        break;
      }

      // Temporal tools
      case "mcp__tierzero__query_temporal_workflows":
        if (rawInput.namespace && rawInput.query) {
          displayText = `namespace:${rawInput.namespace} | query:${rawInput.query}`;
        }
        break;

      case "mcp__tierzero__query_temporal_workflow_history":
        if (rawInput.namespace && rawInput.workflow_id) {
          displayText = `namespace:${rawInput.namespace} | workflow_id:${rawInput.workflow_id}`;
        }
        break;

      // LangSmith tools
      case "mcp__tierzero__query_langsmith_runs":
        if (rawInput.project_name || rawInput.project_id) {
          displayText = `project:${rawInput.project_name || rawInput.project_id}`;
        }
        break;

      case "mcp__tierzero__get_langsmith_run_stats":
        if (rawInput.project_name || rawInput.project_id) {
          displayText = `stats project:${rawInput.project_name || rawInput.project_id}`;
        }
        break;

      // Langfuse tools
      case "mcp__tierzero__query_langfuse_traces":
        if (rawInput.name) {
          displayText = `traces name:${rawInput.name}`;
        } else {
          displayText = `traces from:${rawInput.from_timestamp || ""}`;
        }
        break;

      case "mcp__tierzero__get_langfuse_trace":
        displayText = `trace:${rawInput.trace_id || "unknown"}`;
        break;

      case "mcp__tierzero__get_langfuse_metrics":
        displayText = `metrics view:${rawInput.view || "unknown"}`;
        break;

      // PGAnalyze tools
      case "mcp__tierzero__query_pganalyze_server_info":
        displayText = rawInput.server_id
          ? `server:${rawInput.server_id}`
          : "list servers";
        break;
      case "mcp__tierzero__query_pganalyze_database_info":
        displayText = rawInput.database_id
          ? `database:${rawInput.database_id}`
          : `server:${rawInput.server_id || "unknown"}`;
        break;
      case "mcp__tierzero__query_pganalyze_schema":
        displayText = rawInput.table_id
          ? `table:${rawInput.table_id}`
          : rawInput.index_id
            ? `index:${rawInput.index_id}`
            : `database:${rawInput.database_id || "unknown"}`;
        break;
      case "mcp__tierzero__query_pganalyze_queries":
        displayText = rawInput.query_id
          ? `query:${rawInput.query_id}`
          : `database:${rawInput.database_id || "unknown"}`;
        break;
      case "mcp__tierzero__query_pganalyze_issues": {
        displayText = rawInput.issue_id
          ? `issue:${rawInput.issue_id}`
          : `server:${rawInput.server_id || "unknown"}`;
        if (rawInput.severity) {
          const sev = Array.isArray(rawInput.severity) ? rawInput.severity.join(",") : rawInput.severity;
          displayText += ` severity:${sev}`;
        }
        break;
      }
      case "mcp__tierzero__query_pganalyze_vacuums":
        displayText = rawInput.table_id
          ? `table:${rawInput.table_id}`
          : `server:${rawInput.server_id || "unknown"}`;
        break;
      case "mcp__tierzero__query_pganalyze_logs":
        displayText = `server:${rawInput.server_id || "unknown"}`;
        break;
      case "mcp__tierzero__query_pganalyze_connections":
        displayText = rawInput.database_id
          ? `database:${rawInput.database_id}`
          : `server:${rawInput.server_id || "unknown"}`;
        break;

      // Socket tools
      case "mcp__tierzero__query_socket_alerts": {
        const parts: string[] = [];
        if (rawInput.repo_slug) parts.push(`repo:${rawInput.repo_slug}`);
        if (rawInput.reachability_type) parts.push(`reachability:${rawInput.reachability_type}`);
        if (rawInput.severity) parts.push(`severity:${rawInput.severity}`);
        displayText = parts.length > 0 ? parts.join(" ") : "all alerts";
        break;
      }
      case "mcp__tierzero__list_socket_repos":
        displayText = "list repos";
        break;
      case "mcp__tierzero__query_socket_scan":
        displayText = rawInput.scan_id ? `scan:${rawInput.scan_id}` : "scan";
        break;

      // Web search tools
      case "mcp__tierzero__search_web":
        if (rawInput.query) {
          displayText = String(rawInput.query);
        }
        break;

      case "mcp__tierzero__fetch_web_content":
        if (rawInput.urls && Array.isArray(rawInput.urls)) {
          if (rawInput.urls.length === 1) {
            displayText = rawInput.urls[0];
          } else if (rawInput.urls.length > 1) {
            displayText = `${rawInput.urls.length} URLs`;
          }
        }
        break;

      case "mcp__tierzero__run_capability":
        if (rawInput.capability_name) {
          displayText = `${rawInput.capability_name}`;
        } else {
          displayText = "(Running capability)";
        }
        break;

      // Custom MCP gateway tools
      case "mcp__tierzero__list_custom_mcp_server_tools":
        displayText = "list_tools";
        break;

      case "mcp__tierzero__call_custom_mcp_server_tool":
        displayText = rawInput.tool_name || "call_tool";
        break;

      // Jira MCP tools
      case "mcp__tierzero__get_jira_issue":
        if (rawInput.issue_key) {
          displayText = `issue_key:${rawInput.issue_key}`;
        }
        break;

      case "mcp__tierzero__search_jira_issues":
        if (rawInput.jql) {
          displayText = `jql:${rawInput.jql}`;
        }
        break;

      case "mcp__tierzero__create_jira_issue":
        if (rawInput.summary) {
          displayText = `project:${rawInput.project_key ?? "?"} summary:${rawInput.summary}`;
        }
        break;

      case "mcp__tierzero__update_jira_issue":
        if (rawInput.issue_key) {
          displayText = `issue_key:${rawInput.issue_key}`;
        }
        break;

      case "mcp__tierzero__add_jira_comment":
        if (rawInput.issue_key) {
          displayText = `issue_key:${rawInput.issue_key}`;
        }
        break;

      case "mcp__tierzero__edit_jira_comment": {
        const parts: string[] = [];
        if (rawInput.issue_key) parts.push(`issue_key:${rawInput.issue_key}`);
        if (rawInput.comment_id) parts.push(`comment_id:${rawInput.comment_id}`);
        displayText = parts.join(" ");
        break;
      }

      case "mcp__tierzero__edit_linear_comment":
        if (rawInput.comment_id) {
          displayText = `comment_id:${rawInput.comment_id}`;
        }
        break;

      case "mcp__tierzero__list_jira_projects":
        displayText = "listing projects";
        break;

      // CLI tools
      case "mcp__tierzero__RunCliCmd":
        displayText = `${rawInput.base_cmd || "aws"} ${rawInput.args || ""}`.trim();
        break;

      case "mcp__tierzero__RunCustomCliCmd":
        if (rawInput.command) {
          displayText = rawInput.working_directory
            ? `${rawInput.working_directory}: ${rawInput.command}`
            : rawInput.command;
        }
        break;

      // PostgreSQL tools
      case "mcp__tierzero__RunPostgresQuery":
        displayText = rawInput.query || "";
        break;

      default:
        break;
    }

    // Apply truncation to display text before returning
    return displayText ? truncateString(displayText) : null;
  } catch (e) {
    console.error("Error parsing rawInput:", e);
    return null;
  }
}

/**
 * Extract user-facing display text from an agent thought-process item.
 * Prefers `thinkingSummary` over `thinking`. Structurally typed so both the
 * renderer and the skill cluster can share it without a relay-type import.
 */
export function getThoughtProcessItemDisplayText(item: {
  thinkingSummary: string | null | undefined;
  thinking: string | null | undefined;
}): string | null {
  if (item.thinkingSummary && item.thinkingSummary.trim()) return item.thinkingSummary;
  if (item.thinking && item.thinking.trim()) return item.thinking;
  return null;
}

/**
 * Check if a tool is a Filesystem tool (Read, Grep, Bash, etc.)
 */
export function isFilesystemTool(toolName: string): boolean {
  const filesystemTools = ["Read", "Grep", "Bash", "Write", "Edit", "Glob"];
  return filesystemTools.includes(toolName);
}

/**
 * Skill tool invocations expose `rawInput.skill` (e.g. "investigation", "datadog").
 * Rendered as a labeled chip so the skill name is visible instead of being
 * absorbed into a generic tool cluster.
 */
export function isSkillTool(toolName: string): boolean {
  return toolName === "Skill";
}

/**
 * V1 backend tools that should be shown in v2 UI (temporary allowlist)
 */
const V1_BACKEND_TOOLS = [
  "BatchTool",
  "FetchWebContentTool",
  "GetSlackUsergroupMentionTool",
  "ItemCounterTool",
  "QueryBuildkiteAnnotationsTool",
  "QueryBuildkiteArtifactsTool",
  "QueryBuildkiteBuildsTool",
  "QueryBuildkiteFlakyTestTool",
  "QueryBuildkiteLogsTool",
  "QueryCloudWatchLogsAggregationTool",
  "QueryCloudWatchLogsTool",
  "QueryCloudWatchMetricsTool",
  "QueryCodeTool",
  "QueryDatadogCIPipelineEventsAggregationTool",
  "QueryDatadogCIPipelineEventsTool",
  "QueryDatadogDashboardTool",
  "QueryDatadogErrorTrackingIssueDetailsTool",
  "QueryDatadogErrorTrackingIssuesTool",
  "QueryDatadogEventsTool",
  "QueryDatadogLogsAggregationTool",
  "QueryDatadogLogsTool",
  "QueryDatadogMetricsTool",
  "QueryDatadogMonitorTool",
  "QueryDatadogNotebookTool",
  "QueryDatadogRumAggregationTool",
  "QueryDatadogServiceDependencyTool",
  "QueryDatadogSpansAggregationTool",
  "QueryDatadogSpansTool",
  "QueryGitHubActionsLogsTool",
  "QueryGitHubActionsRunsTool",
  "QueryGrafanaCloudLogsAggregationTool",
  "QueryGrafanaCloudLogsTool",
  "QueryGrafanaCloudMetricsTool",
  "QueryGrafanaCloudSpansAggregationTool",
  "QueryGrafanaCloudSpansTool",
  "QueryNewRelicDashboardTool",
  "QueryNewRelicLogsAggregationTool",
  "QueryNewRelicLogsTool",
  "QueryNewRelicMetricsTool",
  "QueryNewRelicSpansAggregationTool",
  "QueryNewRelicSpansTool",
  "QueryNewRelicTransactionsAggregationTool",
  "QueryNewRelicTransactionsTool",
  "QueryRootlyDashboardTool",
  "QueryRootlyIncidentsTool",
  "QueryRootlyIncidentTool",
  "QueryRootlyAlertsTool",
  "QueryRootlyAlertTool",
  "QueryRootlyOncallTool",
  "QuerySentryIssueEventsTool",
  "QuerySentryIssuesTool",
  "QuerySentryTraceDetailsTool",
  "QuerySentryTracesTool",
  "QuerySlackChannelContentsTool",
  "QuerySlackChannelMetadataTool",
  "QueryTemporalWorkflowHistoryTool",
  "QueryTemporalWorkflowsTool",
  "ScheduleTaskTool",
  "SearchWebTool",
  "TimeConverterTool",
  "UpdateTodoTool",
] as const;

/**
 * Check if a tool should be shown
 * MCP, Filesystem, V1 backend tools, and special tools (TodoWrite, AskUserQuestion) are shown
 */
// Tools that should be silently executed without showing in the thought process
const HIDDEN_MCP_TOOLS = [
  "mcp__tierzero__register_pull_request",
];

export function shouldShowTool(toolName: string): boolean {
  if (HIDDEN_MCP_TOOLS.includes(toolName)) return false;
  return (
    toolName.startsWith("mcp__") ||
    isFilesystemTool(toolName) ||
    isSkillTool(toolName) ||
    toolName === "TodoWrite" ||
    toolName === "AskUserQuestion" ||
    toolName === "Task" ||
    toolName === "Agent" ||
    V1_BACKEND_TOOLS.includes(toolName as any)
  );
}

/**
 * Check if a tool should be rendered as a button
 * MCP tools get buttons, Filesystem tools are hidden (clustered)
 */
export function shouldShowToolAsButton(toolName: string): boolean {
  return toolName.startsWith("mcp__");
}

/**
 * Check if a tool button should be disabled
 * All MCP tools are clickable (none disabled)
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function isToolButtonDisabled(toolName: string): boolean {
  return false;
}

/**
 * Format tool display text with custom info
 * e.g., "QueryDatadogLogs | {filter: ...}"
 */
export function getToolDisplayText(toolCall: any): string {
  const toolName = toolCall.toolUse?.name;
  if (!toolName) return "";

  const rawInput = toolCall.toolUse?.input?.__typename === "GenericToolInput" ? toolCall.toolUse.input.rawInput : null;
  const displayName = getV2ToolDisplayName(toolName, rawInput);
  const customInfo = extractToolCustomInfo(toolCall);

  if (customInfo) {
    return `${displayName} | ${customInfo}`;
  }

  return displayName;
}
